import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { RotationsList } from './rotations-list.component';
import { useRotationsContext } from '../rotations-context';
import styles from './rotations-list.module.scss';
import { AddNewRotation } from '../components/rotations-list-add-rotations.component';
import { Tabs, SearchBox } from 'src/components/design-system';
import { Label } from 'src/components/core/label';

const RotationsTabs = () => {
  const {
    lang,
    myRotations,
    mySchedule,
    allRotations,
    filter,
    acgmeOnly,
    setTab,
    setFilter,
    setAcgmeOnly,
    tab,
    canEditSettings,
    schedules,
    visibleTabKeys,
  } = useRotationsContext();

  const applyFilters = useCallback(
    rotation => {
      if (acgmeOnly && !rotation.isAcgmeRequired) {
        return false;
      }
      if (!filter?.length) {
        return true;
      }
      return rotation.name.toLowerCase().includes(filter.toLowerCase());
    },
    [filter, acgmeOnly],
  );

  const scheduleTabs = useMemo(() => {
    return schedules.map(({ scheduleId, name, rotations }) => ({
      key: scheduleId,
      label: name,
      children: <RotationsList rotations={rotations.filter(applyFilters)} />,
    }));
  }, [applyFilters, schedules]);

  const tabs = useMemo(
    () => [
      {
        key: lang.MY_SCHEDULE,
        label: lang.MY_SCHEDULE,
        children: <RotationsList rotations={mySchedule.filter(applyFilters)} />,
      },
      ...scheduleTabs,
      {
        key: lang.MY_ROTATIONS,
        label: lang.MY_ROTATIONS,
        children: <RotationsList rotations={myRotations.filter(applyFilters)} />,
      },
    ],
    [lang, myRotations, applyFilters, mySchedule, scheduleTabs],
  );

  const hasData = myRotations.length > 0 || mySchedule.length > 0 || allRotations.length > 0;

  if (!hasData && !canEditSettings) {
    return null;
  }

  if (!tab) return null;

  return (
    <div className={styles.viewSelector}>
      {hasData && (
        <>
          <SearchBox
            size="large"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder={lang.FILTER_PLACEHOLDER}
          />

          <div className={styles.filtersRow}>
            <Label
              role="switch"
              aria-checked={acgmeOnly}
              tabIndex={0}
              onClick={() => setAcgmeOnly(!acgmeOnly)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setAcgmeOnly(!acgmeOnly);
                }
              }}
              className={classNames(styles.filterPill, acgmeOnly && styles.filterPillActive)}
              label={lang.ACGME_FILTER_LABEL}
            />
          </div>

          <Tabs
            activeKey={tab}
            items={tabs.filter(({ key }) => visibleTabKeys.includes(key))}
            onChange={setTab}
            tabBarGutter={8}
            moreIcon={null}
          />
        </>
      )}
      {canEditSettings && (
        <div className={styles.addRotationWrapper}>
          <AddNewRotation />
        </div>
      )}
    </div>
  );
};

export { RotationsTabs };
